import { Link } from '../../components/Link.tsx';
import { Hero } from '../../components/marketing/Hero.tsx';
import { MarketingFooter } from '../../components/marketing/MarketingFooter.tsx';
import { CrossLinks } from '../../components/marketing/CrossLinks.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';
import '../../styles/marketing.css';

export function FeatureDocuments() {
  const t = useT();
  useDocumentTitle(t('features-documents.document-title'));

  return (
    <div className="mkt-page">
      <Hero
        eyebrow={t('features-documents.hero.eyebrow')}
        title={t('features-documents.hero.title')}
        lede={t('features-documents.hero.lede')}
      >
        <Link to="/register" className="mkt-btn primary lg">
          {t('features-documents.hero.cta-start')}
        </Link>
        <Link to="/dashboard" className="mkt-btn ghost lg">
          {t('marketing.cta.login')}
        </Link>
      </Hero>

      {/* ChangeBatches build the change as you edit */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-split">
            <div className="mkt-split-body">
              <span className="mkt-eyebrow">{t('features-documents.drafts.eyebrow')}</span>
              <h3>{t('features-documents.drafts.heading')}</h3>
              <p>
                {t('features-documents.drafts.body-1')}
              </p>
              <p>
                {t('features-documents.drafts.body-2')}
              </p>
            </div>
            <div className="mkt-mock" aria-hidden="true">
              <div className="mkt-mock-head">
                <span className="mkt-mock-head-eyebrow">Draft</span>
                <span className="mkt-mock-head-title">Reporting improvements</span>
                <span className="mkt-mock-head-tag">v3 draft</span>
              </div>
              <div className="mkt-mock-body">
                <div className="mkt-ops">
                  <div className="mkt-op add">
                    <span className="mkt-op-tag">ADD</span>
                    <span>Dark mode toggle in settings</span>
                  </div>
                  <div className="mkt-op remove">
                    <span className="mkt-op-tag">REMOVE</span>
                    <span>SMS notification option</span>
                  </div>
                  <div className="mkt-op replace">
                    <span className="mkt-op-tag">REPLACE</span>
                    <span>Password min length → 12 chars</span>
                  </div>
                  <div className="mkt-op add">
                    <span className="mkt-op-tag">ADD</span>
                    <span>Pro tier exports reports to PDF</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lifecycle */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-documents.lifecycle.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-documents.lifecycle.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-documents.lifecycle.lede')}
            </p>
          </div>
          <div className="mkt-pillars">
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-documents.lifecycle.draft.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-documents.lifecycle.draft.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-documents.lifecycle.draft.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-documents.lifecycle.pending.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-documents.lifecycle.pending.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-documents.lifecycle.pending.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-documents.lifecycle.approved.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-documents.lifecycle.approved.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-documents.lifecycle.approved.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spec assembly */}
      <section id="current-spec" className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-split">
            <div className="mkt-mock" aria-hidden="true">
              <div className="mkt-mock-head">
                <span className="mkt-mock-head-eyebrow">Current spec</span>
                <span className="mkt-mock-head-title">Onboarding App</span>
                <span className="mkt-mock-head-tag">v3</span>
              </div>
              <div className="mkt-mock-body">
                <div className="mkt-spec">
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">1.1</span>
                    <span>Users register with email + magic link.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">1.2</span>
                    <span>Email verification required before first login.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">2.1</span>
                    <span>Dark mode toggle available in settings.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">2.2</span>
                    <span>Password minimum length is 12 characters.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">3.1</span>
                    <span>Notifications delivered via email only.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">3.2</span>
                    <span>Users can unsubscribe per document.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">4.1</span>
                    <span>Pro tier exports reports to PDF.</span>
                  </div>
                  <div className="mkt-spec-item">
                    <span className="mkt-spec-num">4.2</span>
                    <span>Dashboards support up to 12 widgets.</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mkt-split-body">
              <span className="mkt-eyebrow">{t('features-documents.spec.eyebrow')}</span>
              <h3>{t('features-documents.spec.heading')}</h3>
              <p>
                {t('features-documents.spec.body-1')}
              </p>
              <p>
                {t('features-documents.spec.body-2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Details tree */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-documents.details.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-documents.details.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-documents.details.lede')}
            </p>
          </div>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              maxWidth: '640px',
              margin: '0 auto',
              fontFamily: 'var(--font-doc)',
              fontSize: '17px',
              lineHeight: 1.9,
              color: 'var(--ink)',
            }}
          >
            <li>
              <strong>Living Room</strong>
              <ul style={{ paddingLeft: '24px', listStyle: 'none' }}>
                <li>
                  Paint
                  <ul style={{ paddingLeft: '24px', listStyle: 'none', color: 'var(--ink-2)' }}>
                    <li>Walls — Benjamin Moore OC-17 &ldquo;White Dove&rdquo;</li>
                    <li>Trim — Semi-gloss, same color</li>
                  </ul>
                </li>
                <li>
                  Lighting
                  <ul style={{ paddingLeft: '24px', listStyle: 'none', color: 'var(--ink-2)' }}>
                    <li>Ceiling — Four recessed, dimmable</li>
                    <li>Accent — Floor lamp by north window</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </section>

      {/* Many documents, one workspace */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-documents.workspace.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-documents.workspace.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-documents.workspace.lede')}
            </p>
          </div>
          <div className="mkt-document-cards">
            <div className="mkt-document-card">
              <div className="mkt-document-card-accent navy" />
              <div className="mkt-document-card-body">
                <h4>Consumer App Spec</h4>
                <div className="mkt-document-card-meta">2,480 details</div>
                <div className="mkt-document-card-rule-label">Approval</div>
                <div className="mkt-document-card-rule">
                  Area-scoped · 6 rules
                </div>
                <div className="mkt-document-card-version">v48 · 2h ago</div>
              </div>
            </div>
            <div className="mkt-document-card">
              <div className="mkt-document-card-accent amber" />
              <div className="mkt-document-card-body">
                <h4>Design System</h4>
                <div className="mkt-document-card-meta">64 components</div>
                <div className="mkt-document-card-rule-label">Approval</div>
                <div className="mkt-document-card-rule">
                  All · App Design Team
                </div>
                <div className="mkt-document-card-version">v9 · yesterday</div>
              </div>
            </div>
            <div className="mkt-document-card">
              <div className="mkt-document-card-accent blue" />
              <div className="mkt-document-card-body">
                <h4>Kitchen Renovation</h4>
                <div className="mkt-document-card-meta">24 requirements</div>
                <div className="mkt-document-card-rule-label">Approval</div>
                <div className="mkt-document-card-rule">
                  Any · The Nguyens
                </div>
                <div className="mkt-document-card-version">v5 · 3 days ago</div>
              </div>
            </div>
            <div className="mkt-document-card">
              <div className="mkt-document-card-accent green" />
              <div className="mkt-document-card-body">
                <h4>Personal Reading List</h4>
                <div className="mkt-document-card-meta">12 entries</div>
                <div className="mkt-document-card-rule-label">Approval</div>
                <div className="mkt-document-card-rule">
                  No approval · auto-approve
                </div>
                <div className="mkt-document-card-version">v11 · today</div>
              </div>
            </div>
          </div>
          <p className="mkt-section-footnote">
            {t('features-documents.workspace.footnote')}
          </p>
        </div>
      </section>

      {/* Cross-links */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <h2 className="mkt-h2">{t('marketing.cta.keep-reading')}</h2>
          </div>
          <CrossLinks
            links={[
              {
                to: '/features/approvals',
                eyebrow: t('marketing.feature.approvals.eyebrow'),
                title: t('marketing.feature.approvals.title'),
                desc: t('features-documents.crosslink.approvals.desc'),
              },
              {
                to: '/features/ai',
                eyebrow: t('marketing.feature.ai.eyebrow'),
                title: t('marketing.feature.ai.title'),
                desc: t('features-documents.crosslink.ai.desc'),
              },
            ]}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-strip">
        <div className="mkt-container-narrow">
          <h2 className="mkt-h2">{t('features-documents.cta.heading')}</h2>
          <p className="mkt-section-lede">
            {t('features-documents.cta.lede')}
          </p>
          <div className="mkt-cta-row">
            <Link to="/register" className="mkt-btn primary lg">
              {t('marketing.cta.signup-free')}
            </Link>
            <Link to="/dashboard" className="mkt-btn ghost lg">
              {t('marketing.cta.login')}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
